import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Use REACT_APP_BACKEND_URL if set, otherwise use current origin (for production with custom domain)
const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Create axios instance for auth
const authApi = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // 401 is expected when not authenticated - no need to log
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/admin/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await authApi.post('/auth/logout');
    } catch (error) {
      // Logout error is not critical - just clear user
    } finally {
      setUser(null);
    }
  };

  const exchangeSession = async (sessionId) => {
    try {
      const response = await authApi.post('/auth/session', { session_id: sessionId });
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Session exchange error - return null
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, exchangeSession, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
